import { Box, Button, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';

import { useT } from '@/hooks';

const MONO_FONT = 'var(--font-plex-mono), IBM Plex Mono, monospace';
const HEADING_FONT = 'var(--font-archivo), Archivo, sans-serif';

interface AmountInputProps {
  amount: string;
  balance: number;
  isAuthenticated: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuickAdd: (addAmount: number | 'max') => void;
}

export function AmountInput({
  amount,
  balance,
  isAuthenticated,
  onChange,
  onQuickAdd,
}: AmountInputProps) {
  const { t } = useT();
  const quickAddSx = {
    flex: 1,
    fontFamily: MONO_FONT,
    fontWeight: 500,
    fontSize: '0.78125rem',
    py: 1.125,
    borderColor: 'divider',
    color: 'text.secondary',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      borderColor: 'primary.main',
      bgcolor: 'action.hover',
      color: 'text.primary',
    },
  } as const;
  const maxButtonSx = {
    ...quickAddSx,
    color: 'primary.main',
    '&:hover': { ...quickAddSx['&:hover'], color: 'primary.main' },
  } as const;

  return (
    <Box sx={{ mb: 2.25 }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.125 }}
      >
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {t('withdrawModal.amountLabel', { defaultValue: 'Amount' })}
        </Typography>
        {isAuthenticated && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.disabled' }}>
            <Typography sx={{ fontFamily: MONO_FONT, fontSize: '0.75rem', fontWeight: 500 }}>
              {t('trade.balanceLabel', { defaultValue: 'Balance' })} ${balance.toFixed(2)}
            </Typography>
          </Box>
        )}
      </Box>
      <TextField
        fullWidth
        size="small"
        value={amount}
        onChange={onChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box
                component="span"
                sx={{ fontFamily: MONO_FONT, fontSize: '1.125rem', color: 'text.disabled' }}
              >
                $
              </Box>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.25,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.default',
            borderRadius: '10px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider',
          },
          '& input': {
            textAlign: 'left',
            fontFamily: MONO_FONT,
            fontWeight: 600,
            fontSize: '1.375rem',
            py: 1.125,
            color: 'text.primary',
          },
        }}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
        <Button variant="outlined" size="small" onClick={() => onQuickAdd(1)} sx={quickAddSx}>
          +$1
        </Button>
        <Button variant="outlined" size="small" onClick={() => onQuickAdd(20)} sx={quickAddSx}>
          +$20
        </Button>
        <Button variant="outlined" size="small" onClick={() => onQuickAdd(100)} sx={quickAddSx}>
          +$100
        </Button>
        <Button variant="outlined" size="small" onClick={() => onQuickAdd('max')} sx={maxButtonSx}>
          {t('trade.max', { defaultValue: 'Max' })}
        </Button>
      </Box>
    </Box>
  );
}
